import { useEffect, useRef, type RefObject } from 'react'

/** Elements that already accept text/keyboard input — never hijack these. */
function isEditableTarget(el: Element | null): boolean {
  if (el instanceof HTMLInputElement) return true
  if (el instanceof HTMLTextAreaElement) return true
  if (el instanceof HTMLSelectElement) return true
  if (el instanceof HTMLElement && el.isContentEditable) return true
  return false
}

export interface UseTypeToFocusOptions {
  /**
   * Ordered focus targets. `refs[0]` is the default when the user hasn't
   * focused any of them yet.
   */
  readonly refs: ReadonlyArray<RefObject<HTMLElement | null>>
  /** Suspend the behaviour (e.g. during a success/exit animation). */
  readonly disabled?: boolean
}

/**
 * "Type anywhere to resume typing." When the user presses a printable key
 * while focus is *outside* any text field, focus the field they last used
 * (or the first field if they haven't focused one yet) so the keystroke
 * lands there instead of being dropped on the floor.
 *
 * Implementation notes:
 *  - A `focusin` listener tracks which target was last focused.
 *  - On a bare printable keydown (no Ctrl/Meta/Alt, single-character
 *    `key`) with focus *not* already in an editable element, we focus the
 *    remembered target synchronously. Browsers route the default text
 *    insertion to whatever element is focused at the end of `keydown`, so
 *    the triggering character lands in the field.
 *  - Listeners subscribe once; live `refs`/`disabled`/`lastIndex` are read
 *    through a ref so re-renders don't re-subscribe.
 */
export function useTypeToFocus({ refs, disabled = false }: UseTypeToFocusOptions): void {
  const stateRef = useRef({ refs, disabled, lastIndex: 0 })
  stateRef.current.refs = refs
  stateRef.current.disabled = disabled

  useEffect(() => {
    function onFocusIn(e: FocusEvent) {
      const idx = stateRef.current.refs.findIndex((r) => r.current === e.target)
      if (idx >= 0) stateRef.current.lastIndex = idx
    }

    function onKeyDown(e: KeyboardEvent) {
      const state = stateRef.current
      if (state.disabled) return
      // Only bare printable characters — let shortcuts and control keys
      // (Tab, Enter, arrows, Backspace, modifiers…) behave normally.
      if (e.ctrlKey || e.metaKey || e.altKey) return
      // `KeyboardEvent.key` is typed `string`, but it is not guaranteed to be
      // one at runtime: password managers, autofill and browser extensions
      // dispatch synthetic events, and a bare `new Event('keydown')` carries no
      // `key` at all. Reading `.length` off that threw "Cannot read properties
      // of undefined" on the management login page in production — the one
      // place a password manager is certain to be active. Check the type, not
      // just the length; TypeScript cannot catch this one for us.
      if (typeof e.key !== 'string' || e.key.length !== 1) return
      // Already typing somewhere editable → leave it alone.
      if (isEditableTarget(document.activeElement)) return
      const target = state.refs[state.lastIndex]?.current ?? state.refs[0]?.current
      target?.focus()
    }

    window.addEventListener('focusin', onFocusIn)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('focusin', onFocusIn)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])
}
